import * as PIXI from 'pixi.js-legacy';

import draw from './util/draw';
import project from './util/project';

import { vec3 } from 'gl-matrix';
import sorter from '../state/tac/tacReducer/sorter';
import constants from '../settings/constants';
import geometry from './util/geometry';

export default class Base extends PIXI.Container {
  isPureContainer(container) {
    return (
      !(container instanceof PIXI.Sprite) && container instanceof PIXI.Container
    );
  }

  calculateProjection() {
    const { item, ratio } = this;

    const outerSize = project.outer(item);
    const faceSize = project.face(item);
    const vertices = project.calculateVertices(item);

    let projectedVertices = vertices.map(project.point);

    const backBottomLeft = projectedVertices[4];

    projectedVertices = projectedVertices.map(vertex =>
      vec3.sub(vec3.create(), vertex, backBottomLeft)
    );

    const frontBottom = projectedVertices[0];

    const scaledVertices = projectedVertices.map(vertex => {
      return [
        (vertex[0] - frontBottom[0]) * ratio,
        (outerSize.height - (vertex[1] - frontBottom[1])) * ratio,
      ];
    });

    this.data = {
      vertices,
      projectedVertices,
      scaledVertices,
      outerSize,
      faceSize,
    };
  }

  drawBox() {
    this.eraseBox();

    const { scaledVertices } = this.data;

    this.box = new PIXI.Container();

    this.box.addChild(draw.fill.face(scaledVertices, 'top', 0x66ff33));
    this.box.addChild(draw.fill.face(scaledVertices, 'front', 0x44dd11));
    this.box.addChild(draw.fill.face(scaledVertices, 'right', 0x22bb00));

    this.addChild(this.box);
  }

  eraseBox() {
    if (this.box) {
      this.box.destroy();

      this.box = null;
    }
  }

  drawMesh(drawHidden) {
    this.eraseMesh();

    const { scaledVertices } = this.data;

    this.mesh = new PIXI.Container();

    if (drawHidden) {
      this.mesh.addChild(draw.outline.face(scaledVertices, 'back'));
      this.mesh.addChild(draw.outline.face(scaledVertices, 'bottom'));
      this.mesh.addChild(draw.outline.face(scaledVertices, 'left'));
    }

    this.mesh.addChild(draw.outline.face(scaledVertices, 'front'));
    this.mesh.addChild(draw.outline.face(scaledVertices, 'top'));
    this.mesh.addChild(draw.outline.face(scaledVertices, 'right'));

    this.addChild(this.mesh);
  }

  eraseMesh() {
    if (this.mesh) {
      this.mesh.destroy();

      this.mesh = null;
    }
  }

  drawOutline(color) {
    this.eraseOutline();

    const { scaledVertices } = this.data;

    const vertices = [
      scaledVertices[0],
      scaledVertices[1],
      scaledVertices[5],
      scaledVertices[6],
      scaledVertices[7],
      scaledVertices[3],
    ];

    this.outline = draw.outline.polygon(vertices, color);

    this.addChild(this.outline);
  }

  eraseOutline() {
    if (this.outline) {
      this.outline.destroy();

      this.outline = null;
    }
  }

  sortLast(sprite, container) {
    if (container.children[container.children.length - 1] !== sprite) {
      const index = container.children.indexOf(sprite);
      container.children.splice(index, 1);
      container.children.push(sprite);
    }
  }

  draggingSpriteIsInTrashcan() {
    return !!(
      this.app?.dragging?.sprite &&
      this.app.isInTrashcan(this.app.dragging.lastDragEventPos)
    );
  }

  isInContainer(sprite, container) {
    return container.children.indexOf(sprite) > -1;
  }

  sortChildren() {
    const container = this.childrenContainer || this;

    const sprites = container.children;

    const itemToSpriteMap = sprites.reduce((map, sprite) => {
      map[sprite.item.itemid] = sprite;
      return map;
    }, {});
    const items = sprites.map(sprite => sprite.item);
    container.children = sorter
      .inDrawOrder(items)
      .map(item => itemToSpriteMap[item.itemid]);

    if (this.draggingSpriteIsInTrashcan()) {
      const { sprite } = this.app.dragging;
      this.isInContainer(sprite, container) && this.sortLast(sprite, container);
    }
  }

  sortChildrenDragging(draggingSprite, draggingItem, draggingMode) {
    const container = this.childrenContainer || this;
    const hasRoomSlots = this.app.dragging?.slots?.filter(
      slot => !slot.parent
    ).length;

    if (
      draggingMode === constants.DRAG_MODE.INSERT ||
      (draggingMode === constants.DRAG_MODE.MIXED && !hasRoomSlots) ||
      this.draggingSpriteIsInTrashcan()
    ) {
      this.sortLast(draggingSprite, container);
      return;
    }

    const collisions = container.children.filter(
      sibling =>
        sibling !== draggingSprite &&
        geometry.collides(draggingSprite.getBounds(), sibling.getBounds())
    );
    const currentIndex = container.children.indexOf(draggingSprite);
    const result = collisions.reduce(
      (result, collision) => {
        const isBehind = project.isBehind(draggingItem, collision.item);
        const index = container.children.indexOf(collision);

        if (isBehind && index < result.below) {
          result.below = index;
        } else if (!isBehind && index > result.above) {
          result.above = index;
        }

        return result;
      },
      { above: -Infinity, below: Infinity }
    );

    if (result.below <= result.above) {
      // bad result, calling regular sort
      return this.sortChildren();
    }

    if (currentIndex > result.above && currentIndex < result.below) {
      // is already fine do nothing
      return;
    }

    if (currentIndex <= result.above) {
      // place it above
      container.children.splice(currentIndex, 1);
      container.children.splice(result.above, 0, draggingSprite);
      return;
    }

    if (currentIndex >= result.below) {
      // place it below
      container.children.splice(currentIndex, 1);
      container.children.splice(result.below, 0, draggingSprite);
      return;
    }

    return;
  }
}
